import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { Typography } from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import FeedbackIcon from '@material-ui/icons/Feedback';

// CONSTANTS
import { PaymentVariant } from '@/constants/subscription';

// UTILITY
import { analyticsCompletedSubscriptionSignUp } from '@/utility/analytics';

// STORE
import useActions from '@/hooks/useActions';
import { SubscriptionActions, UserActions } from '@/store/actions';

// HOC
import withStripeElements from '@/hoc/withStripeElements';

// HELP
import SubscriptionStepTemplate from '../SubscriptionStepTemplate';
import StripePaymentForm from './help/StripePaymentForm';
import SRFundsPaymentForm from './help/SRFundsPaymentForm';

function getAddonsIds(addons) {
  return addons.map(addon => addon.plans[0].id);
}

const alertIconMapping = {
  error: <FeedbackIcon />,
};

const transComponents = {
  p: <Typography variant="body1" />,
  b: <Typography variant="body2" component="b" />,
  ul: <ul style={{ paddingLeft: 25, margin: 0 }} />,
  li: <li />,
};

function PaymentForm(props) {
  const {
    stripe,
    stripeError,
    elements,
    onNext,
    dictionary,
    loading,
    cardsLoading,
    previewLoading,
    paymentLoading,
    analyticsLocation,
    partner,
    errors,
    breadcrumbs,
    user,
    specOfferValue,
  } = props;

  const [t] = useTranslation();
  const [virtualCard] = useSelector(({ user: userStore }) => [userStore.virtualCard]);
  const [getCards, getSubscriptionPreview, addSubscriptionPayment] = useActions([
    UserActions.userGetCards.action,
    data => SubscriptionActions.getSubscriptionPreview.action({ data }),
    SubscriptionActions.addSubscriptionPayment.action,
  ]);

  const [paymentVariant, setPaymentVariant] = useState(PaymentVariant.CARD);

  const {
    selectedProduct,
    selectedPlan,
    selectedAddons,
    subscription,
    subscription_end_date,
    app,
    app_account,
    app_account_temp,
    totalPrice,
  } = dictionary;
  const isDeviceProductType = selectedProduct.type === 'device_product';
  const errorMessage =
    errors.payment || errors.captcha || (stripeError && t('notification.encProblem'));
  const hasError = !!errorMessage;
  const hasTrial = selectedProduct?.has_trial;
  const showSuccessAlert = !hasError && !!subscription_end_date;
  const price = selectedPlan?.price;
  const interval = selectedPlan?.interval;
  const accEmail = app_account?.email || app_account_temp?.payload?.email || '';
  const accZipcode = app_account?.zipcode || app_account_temp?.payload?.zip || '';
  const useVirtualCardForSubscription = Boolean(virtualCard?.allowed_for_subscriptions);
  const cardReqParams = { app_id: app.id };
  const isPartner = !!Object.keys(partner).length;
  const rewardData = user?.eligible_streaming_reward || {};
  const skipCheckoutFlow = subscription?.registration_meta?.skip_checkout || false;
  const isRokuSD = subscription?.merchant?.slug === 'roku_streaming_device';
  const paymentData = {
    type: subscription.merchant.slug,
    plan_id: selectedPlan.id,
    addons: getAddonsIds(selectedAddons || []),
    app_id: app.id,
    partner_id: partner?.partner_id || null,
    claim_special_offer: specOfferValue,
  };

  useEffect(() => {
    getSubscriptionPreview(paymentData);
    getCards(cardReqParams);
  }, []);

  useEffect(() => {
    if (
      (isPartner && partner.slug === rewardData.partner) ||
      (!isPartner && rewardData.partner === null)
    ) {
      setPaymentVariant(
        useVirtualCardForSubscription ? PaymentVariant.VIRTUAL_CARD : PaymentVariant.CARD,
      );
    }
  }, [useVirtualCardForSubscription, isPartner, rewardData]);

  if (!paymentLoading && cardsLoading) return null;

  function onSubmit(values) {
    const { payment_method_id } = values;
    const addons = selectedAddons
      ? selectedAddons.map(addon => ({
          addon_id: addon.id,
          addon_plan_id: addon.plans[0].id,
        }))
      : [];

    if (!stripe || !elements) {
      return;
    }

    function confirmCardPayment(client_secret) {
      return stripe.confirmCardPayment(client_secret).then(({ error, paymentIntent }) => {
        if (error) {
          throw error;
        }

        return paymentIntent;
      });
    }

    function callback() {
      analyticsCompletedSubscriptionSignUp({
        location: analyticsLocation,
        app_id: paymentData.id,
        product_id: selectedProduct.id,
        plan_id: paymentData.plan_id,
        price: totalPrice,
        addons,
        email: accEmail,
        zip_code: accZipcode,
      });

      onNext();
    }

    if (payment_method_id) {
      addSubscriptionPayment({
        paymentVariant,
        paymentData,
        confirmCardPayment,
        payment_method_id,
        callback,
      });

      return;
    }

    const card = elements.getElement('cardNumber');

    function createPaymentMethod() {
      return stripe
        .createPaymentMethod({
          card,
          type: 'card',
          billing_details: {
            address: {
              postal_code: values.zipcode,
            },
          },
        })
        .then(({ error, paymentMethod }) => {
          if (error) {
            throw error;
          }

          return paymentMethod;
        });
    }

    addSubscriptionPayment({
      paymentVariant,
      paymentData,
      createPaymentMethod,
      confirmCardPayment,
      callback,
    });
  }

  function getDesctiptionContext() {
    let context = '';

    if (paymentVariant === PaymentVariant.VIRTUAL_CARD) context = 'virtualCard';

    if (isRokuSD) context = 'roku';

    return context;
  }

  const formLoading = !stripe || cardsLoading || previewLoading;
  const disabled = loading || formLoading || !!errors.captcha || stripeError;

  const alert =
    showSuccessAlert &&
    (hasTrial ? (
      <Alert severity="success" className="primary sizeLarge">
        <AlertTitle>{t('subscription.notifications.payment_confirmed')}</AlertTitle>
        <Trans
          i18nKey="subscription.notifications.trial"
          values={{ price, interval }}
          components={transComponents}
        />
      </Alert>
    ) : (
      <Alert severity="success" className="primary sizeLarge">
        <AlertTitle>{t('subscription.notifications.thanks_for_purchase')}</AlertTitle>
        <Trans i18nKey="subscription.notifications.card_charged" components={transComponents} />
      </Alert>
    ));

  const errorAlert = hasError && (
    <Alert severity="error" iconMapping={alertIconMapping} className="primary submit-error-alert">
      <AlertTitle>{t('subscription.notifications.error_default')}</AlertTitle>
      <Typography variant="body1">
        <Trans components={transComponents}>{errorMessage}</Trans>
      </Typography>
    </Alert>
  );

  return (
    <SubscriptionStepTemplate
      alert={alert}
      breadcrumbs={breadcrumbs}
      title={t(
        skipCheckoutFlow
          ? 'subscription.srWithoutBalance.payment.title'
          : 'subscription.payment.title',
      )}
      description={t(
        skipCheckoutFlow
          ? 'subscription.srWithoutBalance.payment.subtitle'
          : 'subscription.payment.subtitle',
        {
          app: app.name,
          context: getDesctiptionContext(),
        },
      )}
      error={errorAlert}
      form={
        !stripeError &&
        (virtualCard && paymentVariant === PaymentVariant.VIRTUAL_CARD ? (
          <>
            <SRFundsPaymentForm
              totalPrice={dictionary.totalPrice}
              partner={partner}
              onSubmit={onSubmit}
              virtualCard={virtualCard}
              cardReqParams={cardReqParams}
              disabled={disabled}
              loading={formLoading}
              price={price}
              trialDays={selectedProduct?.trial_days}
              isSkipCheckoutFlow={skipCheckoutFlow}
              stripeInitValues={{ zipcode: accZipcode }}
            />
            <Typography className="p5 m-t-15">
              {t('subscription.payment.cancellationPolicy', {
                action_name: t('common.purchase'),
                name: app.name,
              })}
            </Typography>
          </>
        ) : (
          <>
            <StripePaymentForm
              onSubmit={onSubmit}
              initialValues={{ zipcode: accZipcode }}
              loading={formLoading}
              disabled={disabled}
              price={price}
              trialDays={selectedProduct?.trial_days}
              productType={selectedProduct.type}
            />
            <Typography className="p5 m-t-15">
              {t(
                `subscription.payment.${
                  isRokuSD ? 'cancellationPolicyRoku' : 'cancellationPolicy'
                }`,
                {
                  action_name: isDeviceProductType ? t('common.purchase') : t('common.subscribe'),
                  name: app.name,
                },
              )}
            </Typography>
          </>
        ))
      }
    />
  );
}

PaymentForm.defaultProps = {
  errors: {},
  specOfferValue: '',
};

PaymentForm.propTypes = {
  stripe: PropTypes.object,
  stripeError: PropTypes.string,
  elements: PropTypes.object,
  breadcrumbs: PropTypes.node,
  partner: PropTypes.object,
  errors: PropTypes.object,
  dictionary: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  cardsLoading: PropTypes.bool.isRequired,
  paymentLoading: PropTypes.bool.isRequired,
  previewLoading: PropTypes.bool.isRequired,
  onNext: PropTypes.func.isRequired,
  user: PropTypes.object,
  analyticsLocation: PropTypes.string.isRequired,
  specOfferValue: PropTypes.string,
};

export default withStripeElements(PaymentForm);
